/**
 * ExamLayout — mobile-first exam shell
 * Sidebar → bottom drawer on mobile, fixed panel on desktop
 */

import { useState }       from 'react'
import { useTranslation } from 'react-i18next'
import {
  Send, ChevronLeft, ChevronRight,
  LayoutGrid, X, Moon, Sun,
} from 'lucide-react'
import CountdownTimer from '@/components/ui/CountdownTimer'
import ConfirmModal   from '@/components/ui/ConfirmModal'
import ExamGuard      from '@/components/exam/ExamGuard'
import { useTheme }   from '@/context/ThemeContext'

export default function ExamLayout({
  exam, resultMeta, totalQ, currentIdx,
  answers, saveStatus, questions,
  onFinish, onPrev, onNext, onJump, onExpire, children,
}) {
  const { t } = useTranslation()
  const { isDark, toggleTheme } = useTheme()
  const [showConfirm,  setShowConfirm]  = useState(false)
  const [drawerOpen,   setDrawerOpen]   = useState(false)

  const answeredSet   = new Set(Object.keys(answers).map(Number))
  const answeredCount = answeredSet.size
  const progressPct   = totalQ > 0 ? (answeredCount / totalQ) * 100 : 0

  // Group questions into consecutive subject blocks (e.g. Math 1-10, Physics 11-20)
  const subjectGroups = []
  questions.forEach((q, idx) => {
    const name = q.subject_name ?? q.question_bank_name ?? null
    const last = subjectGroups[subjectGroups.length - 1]
    if (last && last.name === name) {
      last.endIdx = idx
      last.count += 1
    } else {
      subjectGroups.push({ name, startIdx: idx, endIdx: idx, count: 1 })
    }
  })
  const hasMultipleSubjects = subjectGroups.filter(g => g.name).length > 1
  const currentSubject = hasMultipleSubjects
    ? subjectGroups.find(g => currentIdx >= g.startIdx && currentIdx <= g.endIdx)
    : null

  const timeLimitSeconds =
    (resultMeta?.time_limit_minutes ?? exam?.time_limit_minutes)
      ? (resultMeta?.time_limit_minutes ?? exam?.time_limit_minutes) * 60
      : null
  const startedAt = resultMeta?.start_time ?? null

  function jump(idx) { onJump(idx); setDrawerOpen(false) }

  function qBtnCls(q, idx) {
    const isCurrent  = idx === currentIdx
    const isAnswered = answeredSet.has(q.id)
    if (isCurrent)  return 'bg-blue-600 text-white shadow shadow-blue-500/30'
    if (isAnswered) return isDark
      ? 'bg-blue-900/40 text-blue-300 border border-blue-700/50'
      : 'bg-blue-50 text-blue-600 border border-blue-200'
    return isDark
      ? 'bg-[#2a2a2e] text-gray-400 border border-[#3a3a3e]'
      : 'bg-white text-gray-500 border border-gray-200'
  }

  return (
    <div
      className="flex flex-col h-full bg-[#f0f2f5] dark:bg-[#161618] text-gray-900 dark:text-white"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >

      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <header className="flex-none bg-white dark:bg-[#1e1e22] border-b border-gray-200 dark:border-[#2e2e33] z-20">
        <div className="flex items-center gap-2 px-3 py-2.5">

          {/* Grid button → opens drawer */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="relative flex-none p-2 rounded-lg bg-gray-100 dark:bg-[#2a2a2e] text-gray-500 dark:text-gray-400"
          >
            <LayoutGrid size={17} />
            {answeredCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[9px] font-bold flex items-center justify-center">
                {answeredCount}
              </span>
            )}
          </button>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate leading-tight text-gray-900 dark:text-white">
              {currentSubject?.name ?? exam?.title ?? t('exam.title')}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              {currentIdx + 1} / {totalQ} savol
            </p>
          </div>

          {/* Timer */}
          <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-[#2a2a2e] rounded-lg px-2.5 py-1.5 flex-none">
            <CountdownTimer
              totalSeconds={timeLimitSeconds}
              startedAt={startedAt}
              onExpire={onExpire}
            />
          </div>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="flex-none p-2 rounded-lg bg-gray-100 dark:bg-[#2a2a2e] text-gray-400 dark:text-gray-500"
          >
            {isDark ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          {/* Submit */}
          <button
            onClick={() => setShowConfirm(true)}
            className="flex-none flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs font-bold px-3 py-2 rounded-lg transition-all active:scale-95 whitespace-nowrap shadow-sm"
          >
            <Send size={13} />
            Yakunlash
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-[#2a2a2e]">
          <div
            className="h-full bg-blue-500 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </header>

      {/* ── CONTENT ──────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto px-4 py-5">
        <ExamGuard>
          <div className="max-w-xl mx-auto">
            {children}
          </div>
        </ExamGuard>
      </main>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer className="flex-none bg-white dark:bg-[#1e1e22] border-t border-gray-200 dark:border-[#2e2e33] z-20">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={onPrev}
            disabled={currentIdx === 0}
            className="flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-[#2a2a2e] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#3a3a3e] disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 transition-all"
          >
            <ChevronLeft size={16} />
            Oldingi
          </button>

          <div className="flex-1 text-center">
            <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
              {answeredCount} / {totalQ} javob berildi
            </span>
          </div>

          <button
            onClick={currentIdx === totalQ - 1 ? () => setShowConfirm(true) : onNext}
            className={[
              'flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-semibold active:scale-95 transition-all',
              currentIdx === totalQ - 1
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-[#2a2a2e] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#3a3a3e]',
            ].join(' ')}
          >
            {currentIdx === totalQ - 1 ? 'Yakunlash' : 'Keyingi'}
            <ChevronRight size={16} />
          </button>
        </div>
      </footer>

      {/* ── QUESTION DRAWER (bottom sheet) ───────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={() => setDrawerOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* Sheet */}
          <div
            className="relative bg-white dark:bg-[#1e1e22] rounded-t-2xl shadow-2xl max-h-[70vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Sheet handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            {/* Sheet header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-[#2a2a2e]">
              <div>
                <span className="text-sm font-bold text-gray-800 dark:text-white">Savollar</span>
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{answeredCount}/{totalQ} javob berildi</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Question grid */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {hasMultipleSubjects ? (
                <div className="space-y-4">
                  {subjectGroups.map((group, gi) => (
                    <div key={gi}>
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                        {group.name} <span className="font-normal normal-case">({group.count} ta savol)</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {questions.slice(group.startIdx, group.endIdx + 1).map((q, i) => {
                          const idx = group.startIdx + i
                          return (
                            <button
                              key={q.id}
                              onClick={() => jump(idx)}
                              className={[
                                'w-10 h-10 rounded-xl text-sm font-semibold transition-all active:scale-90 flex-none',
                                qBtnCls(q, idx),
                              ].join(' ')}
                            >
                              {idx + 1}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {questions.map((q, idx) => (
                    <button
                      key={q.id}
                      onClick={() => jump(idx)}
                      className={[
                        'w-10 h-10 rounded-xl text-sm font-semibold transition-all active:scale-90 flex-none',
                        qBtnCls(q, idx),
                      ].join(' ')}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-5 px-5 py-3 border-t border-gray-100 dark:border-[#2a2a2e]">
              {[
                { cls: 'bg-blue-600', label: 'Joriy' },
                { cls: isDark ? 'bg-blue-900/40 border border-blue-700/50' : 'bg-blue-50 border border-blue-200', label: 'Javob berildi' },
                { cls: isDark ? 'bg-[#2a2a2e] border border-[#3a3a3e]' : 'bg-white border border-gray-200', label: "Bo'sh" },
              ].map(({ cls, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className={`w-3.5 h-3.5 rounded-md flex-none ${cls}`} />
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM MODAL ────────────────────────────────────────────── */}
      <ConfirmModal
        open={showConfirm}
        title={t('exam.confirmSubmit')}
        message={
          answeredCount < totalQ
            ? t('exam.confirmSubmitDetail', { unanswered: totalQ - answeredCount })
            : t('exam.allAnswered')
        }
        confirmLabel={t('exam.submitExam')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { setShowConfirm(false); onFinish() }}
        onCancel={() => setShowConfirm(false)}
        danger={answeredCount < totalQ}
      />
    </div>
  )
}
